import { useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import InputSelect from "core/inputs/input-select/InputSelect";
import InputDate from "core/inputs/input-date/InputDate";
import FafField from "./FafField";
import FafBudgetWarning from "./FafBudgetWarning";
import NeedForm from "./NeedForm";
import FafCandidateSearch from "./FafCandidateSearch";
import {
  candidateToQualifFields,
  hasCandidate,
  isNeedEmpty,
  newQualifCandidate,
  newQualifDateInputId,
  qualifFieldErrors,
} from "utils/fafHelper";

// Form for a new qualification: DoYouBuzz candidate + need reference
// (existing need XOR creating a new need via the reused NeedForm).
// Aligned with the back: no status at creation (set later via the weekly review), but a
// qualification date, which the back requires.
// `showErrors` triggers the per-field EOS error affordance (cf. FafField).
const QualifForm = ({
  id,
  qualif,
  onChange,
  showErrors,
  lang,
  needs,
  needBudgetReached,
  noActiveNeeds,
  onRemove,
}) => {
  // No attachable active need → creation is mandatory (the back has nothing to reference).
  // We force use_new_need in state (single source for the payload/validation) only for a
  // NEW qualif (without id): an already-created qualif references a frozen need (no update).
  // `need_creation_forced` marks this as an *automatic* switch (vs a user choice), so the
  // symmetric effect below can revert it. The !use_new_need guard prevents any loop.
  useEffect(() => {
    if (noActiveNeeds && !qualif.id && !qualif.use_new_need)
      onChange({ use_new_need: true, need_id: null, need_creation_forced: true });
  }, [noActiveNeeds, qualif.id, qualif.use_new_need, onChange]);

  // Symmetric to the force above: a need became available again (e.g. re-activated via the
  // weekly review) while creation was only *forced* by the absence of needs, and the draft
  // need is still untouched → fall back to the select proposed by default. A creation the
  // user chose explicitly (need_creation_forced false) or already filled is left as-is.
  // Gated on the positive `needs.length` signal (not `!noActiveNeeds`): the active list is
  // empty during its reload, so this avoids a premature revert flash before the fetch lands.
  useEffect(() => {
    if (
      needs.length > 0 &&
      !qualif.id &&
      qualif.use_new_need &&
      qualif.need_creation_forced &&
      isNeedEmpty(qualif.new_need)
    )
      onChange({ use_new_need: false, need_creation_forced: false });
  }, [
    needs.length,
    qualif.id,
    qualif.use_new_need,
    qualif.need_creation_forced,
    qualif.new_need,
    onChange,
  ]);

  // Per-field errors derived from the single fafHelper source (no local re-derivation).
  const candidateInvalid = showErrors && !hasCandidate(newQualifCandidate(qualif));
  // The need reference is flagged only for the « existing need » select:
  // when creating a need, the nested NeedForm carries its own per-field errors.
  const needRefInvalid = showErrors && !qualif.use_new_need && !qualif.need_id;
  const dateInvalid = showErrors && qualifFieldErrors(qualif).qualification_date;

  return (
    <div className="faf-need-form">
      {onRemove && (
        <div className="faf-need-form-head">
          <button
            type="button"
            className="faf-need-remove"
            onClick={onRemove}
            data-testid={`faf-qualif-${id}-remove`}
          >
            <FormattedMessage
              id="tools_manager_faf_remove"
              defaultMessage="Supprimer"
            />
          </button>
        </div>
      )}
      <div className="faf-fields-2">
        <FafField
          label={{
            id: "tools_manager_faf_qualif_candidate",
            defaultMessage: "Candidat",
          }}
          required
          invalid={candidateInvalid}
          errorMessage="tools_manager_faf_candidate_required"
        >
          <FafCandidateSearch
            candidate={newQualifCandidate(qualif)}
            // No qualif update route: an already-created qualif keeps its candidate.
            disabled={!!qualif.id}
            onChange={(candidate) => onChange(candidateToQualifFields(candidate))}
            dataTestId={`faf-qualif-${id}-candidate`}
          />
        </FafField>

        <FafField
          label={{
            id: "tools_manager_faf_qualif_date_label",
            defaultMessage: "Date de qualification",
          }}
          required
          invalid={dateInvalid}
          errorMessage="tools_manager_faf_qualif_date_required"
        >
          <InputDate
            id={newQualifDateInputId(id)}
            name={`faf-qualif-${id}-date`}
            value={qualif.qualification_date || ""}
            lang={lang}
            // No qualif update route: an already-created qualif keeps its date.
            readOnly={!!qualif.id}
            // Browsing months must not commit a date on its own.
            onMonthChange={undefined}
            onYearChange={undefined}
            // Focused on a failed submit: keeps the calendar from opening off screen.
            preventOpenOnFocus
            onChange={(e) => onChange({ qualification_date: e.target.value })}
          />
        </FafField>
      </div>

      {/* Attachment to an existing need (the select) XOR on-the-fly creation (NeedForm).
          The « Besoin concerné » label accompanies only the select: when creating, the NeedForm
          carries its own labels, so we hide this field entirely. */}
      {qualif.use_new_need ? (
        <NeedForm
          id={`q-${id}`}
          need={qualif.new_need}
          onChange={(patch) =>
            onChange({ new_need: { ...qualif.new_need, ...patch } })
          }
          showErrors={showErrors}
          embedded
          // No attachable active need → creation is forced: no « Supprimer »
          // (the user cannot go back to an empty select anyway).
          onRemove={
            noActiveNeeds
              ? undefined
              : () =>
                  onChange({
                    use_new_need: false,
                    need_id: null,
                    need_creation_forced: false,
                  })
          }
        />
      ) : (
        <FafField
          label={{
            id: "tools_manager_faf_qualif_need",
            defaultMessage: "Besoin concerné",
          }}
          required
          invalid={needRefInvalid}
        >
          {/* Empty option at the top: allows deselecting (back to null) and
              re-displaying the « Créer un nouveau besoin » link. */}
          <InputSelect
            id={`faf-qualif-need-${id}`}
            dataTestId={`faf-qualif-${id}-need-select`}
            name="need_id"
            options={[
              {
                value: "",
                label: {
                  id: "tools_manager_faf_qualif_need_empty",
                  defaultMessage: "Aucun besoin",
                },
              },
              ...needs.map((n) => ({ value: n.id, label: n.title })),
            ]}
            value={qualif.need_id ?? ""}
            setValue={(_name, val) => onChange({ need_id: val || null })}
          />
          {/* The creation link only makes sense if NO need is selected
              (otherwise we hide it). Budget of 3 needs reached → we explain why. */}
          {!qualif.need_id &&
            (needBudgetReached ? (
              <FafBudgetWarning />
            ) : (
              <div className="faf-create-need">
                <button
                  type="button"
                  className="faf-need-remove"
                  data-testid={`faf-qualif-${id}-create-need`}
                  onClick={() =>
                    onChange({
                      use_new_need: true,
                      need_id: null,
                      need_creation_forced: false,
                    })
                  }
                >
                  <FormattedMessage
                    id="tools_manager_faf_qualif_create_need"
                    defaultMessage="Créer un nouveau besoin"
                  />
                </button>
              </div>
            ))}
        </FafField>
      )}
    </div>
  );
};

QualifForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  qualif: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool,
  lang: PropTypes.string,
  needs: PropTypes.array.isRequired,
  needBudgetReached: PropTypes.bool,
  noActiveNeeds: PropTypes.bool,
  onRemove: PropTypes.func,
};

export default QualifForm;
